import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { API } from "@/lib/api";
import { STRINGS } from "@/lib/i18n";
import { Brand } from "@/components/Brand";
import SeoHead from "@/components/SeoHead";
import PublicFooter from "@/components/PublicFooter";
import AlternativeUses from "@/components/AlternativeUses";
import { CheckCircle2, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { NumberField, validateNumeric } from "@/components/NumberField";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { StreetViewPhoto } from "@/components/StreetViewPhoto";

export default function Provider() {
  const loc = useLocation();
  const lang = loc.pathname.startsWith("/es/") ? "es" : "en";
  const t = STRINGS[lang] || STRINGS.en;
  const [form, setForm] = useState({ address: "", bedrooms: "3", bathrooms: "2", sqft: "1500", occupied: false, zip_code: "" });
  const [errors, setErrors] = useState({});
  const [verified, setVerified] = useState(null);
  const [result, setResult] = useState(null);
  const numeric = () => ({ bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms), sqft: Number(form.sqft) });
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [clickedUseType, setClickedUseType] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", organization: "", notes: "" });
  const [contactErrors, setContactErrors] = useState({});
  const inputCls = (bad) => `w-full border rounded-md px-3 py-3 text-base min-h-[48px] bg-white ${bad ? "border-red-500" : "border-slate-300"}`;
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {
      bedrooms: validateNumeric(form.bedrooms, { min: 1, max: 20, label: t.provider_beds, lang }),
      bathrooms: validateNumeric(form.bathrooms, { min: 0.5, max: 20, label: t.provider_baths, lang }),
      sqft: validateNumeric(form.sqft, { min: 100, max: 50000, label: t.provider_sqft, lang }),
    };
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;
    try {
      const r = await fetch(`${API}/public/provider/match`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, ...numeric(), address_verified: !!(verified && verified.address_verified), address_id: verified ? verified.id : "" }) });
      if (!r.ok) throw new Error();
      setResult(await r.json());
    } catch { toast.error(lang === "es" ? "No se pudo comparar — inténtelo de nuevo." : "Could not run the match — please try again."); }
  };

  const openHandoff = (useTypeSlug = "") => {
    setClickedUseType(useTypeSlug || "");
    setHandoffOpen(true);
    // Scroll to the handoff form on next tick so mobile users see it.
    setTimeout(() => {
      const el = document.getElementById("provider-handoff-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handoff = async () => {
    const errs = {};
    if (!contact.name.trim()) errs.name = t.owner_err_name;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(contact.email.trim())) errs.email = t.owner_err_email;
    setContactErrors(errs);
    if (Object.keys(errs).length) return;
    const programs_shown = ((result && result.fits) || []).slice(0, 12).map(f => ({ id: f.program_id, name: f.name }));
    const alternatives_shown = ((result && result.alternatives) || []).map(a => a.slug).filter(Boolean);
    try {
      const r = await fetch(`${API}/public/provider/handoff`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...contact, email: contact.email.trim(),
          address: form.address, ...numeric(), occupied: form.occupied,
          address_verified: !!(verified && verified.address_verified), address_id: verified ? verified.id : "", county: verified ? verified.county : "",
          market: "San Antonio", language: lang, programs_shown, alternatives_shown,
          clicked_use_type: clickedUseType || "",
        }) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || ""); }
      setSent(true);
    } catch (e) { toast.error(e.message || (lang === "es" ? "No se pudo enviar — inténtelo de nuevo." : "Could not send — please try again.")); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900" data-testid="provider-page" lang={lang}>
      <SeoHead
        title={lang === "es" ? "Para propietarios · Throughline · San Antonio" : "For property owners · Throughline · San Antonio"}
        description={lang === "es"
          ? "Compare confidencialmente su propiedad contra programas de vivienda activos en el condado de Bexar. Sin publicación, sin listado."
          : "Confidentially match your property against active housing programs in Bexar County. Nothing is listed, nothing is published — reversible at any point."}
        canonicalPath={lang === "es" ? "/es/provider" : "/provider"}
        hreflang={[
          { lang: "en", path: "/provider" },
          { lang: "es", path: "/es/provider" },
          { lang: "x-default", path: "/provider" },
        ]}
      />
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3"><Link to="/"><Brand variant="dark" size={18} /></Link></div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <h1 className="text-3xl md:text-4xl font-bold leading-tight" data-testid="provider-title">{t.provider_title}</h1>
        <p className="mt-3 text-sm md:text-base text-slate-700 leading-relaxed">{t.provider_lede}</p>
        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-900 flex gap-2" data-testid="provider-privacy"><Info className="w-4 h-4 shrink-0" /><span>{t.privacy_note}</span></div>

        <form onSubmit={submit} className="mt-6 p-4 md:p-6 bg-white border border-slate-200 rounded-lg space-y-4" data-testid="provider-form">
          <AddressAutocomplete id="p-addr" label={t.provider_address} value={form.address} lang={lang} testId="provider-address"
            onChange={v => setForm(f => ({ ...f, address: v }))}
            onVerified={rec => { setVerified(rec); if (rec && rec.address_verified) setForm(f => ({ ...f, zip_code: f.zip_code || (rec.matched_address.match(/\b\d{5}\b/) || [""])[0] })); }} />
          {verified && verified.address_verified && <StreetViewPhoto addressId={verified.id} lang={lang} />}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-semibold mb-1" htmlFor="p-zip">{t.provider_zip}</label>
              <input id="p-zip" inputMode="numeric" pattern="[0-9]*" maxLength={5} value={form.zip_code} onChange={e=>setForm({...form, zip_code:e.target.value.replace(/\D/g,'').slice(0,5)})} className="w-full border border-slate-300 rounded-md px-3 py-3 text-base min-h-[48px] bg-white" data-testid="provider-zip" placeholder="78108" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <NumberField id="p-beds" label={t.provider_beds} value={form.bedrooms} onChange={v=>setForm({...form, bedrooms:v})} error={errors.bedrooms} testId="provider-beds" />
            <NumberField id="p-baths" label={t.provider_baths} value={form.bathrooms} onChange={v=>setForm({...form, bathrooms:v})} decimal error={errors.bathrooms} testId="provider-baths" />
            <div className="col-span-2 sm:col-span-1">
              <NumberField id="p-sqft" label={t.provider_sqft} value={form.sqft} onChange={v=>setForm({...form, sqft:v})} error={errors.sqft} testId="provider-sqft" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.occupied} onChange={e=>setForm({...form, occupied:e.target.checked})} data-testid="provider-occupied" /> {t.provider_occupied}
          </label>
          <button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md py-3 min-h-[48px]" data-testid="provider-submit">{t.provider_submit}</button>
        </form>

        {result && (
          <section className="mt-8" data-testid="provider-fits">
            <h2 className="text-xl font-bold mb-3">{t.provider_fits_title}</h2>
            <div className="space-y-3">
              {(result.fits || []).slice(0, 12).map(f => (
                <article key={f.program_id} className="p-4 bg-white border border-slate-200 rounded-lg" data-testid={`provider-fit-${f.program_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-bold text-lg leading-tight">{f.name}</h3>
                    {f.recommended && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-wider">Fits</span>}
                  </div>
                  <p className="text-sm text-slate-700 mt-1">{f.who_it_serves}</p>
                  <div className="mt-2 text-sm"><span className="font-semibold">Typical payment:</span> {f.typical_payment}</div>
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t.provider_must_be_true}</div>
                    <ul className="mt-1 text-sm text-slate-800 space-y-1">
                      {(f.must_be_true || []).map((m, i) => <li key={i} className="flex gap-1.5"><CheckCircle2 className="w-4 h-4 text-slate-400 shrink-0 mt-0.5"/>{m}</li>)}
                    </ul>
                  </div>
                </article>
              ))}
            </div>

            {/* Alternative Uses — the ordinary and commercial options this property could also serve. */}
            {(result.alternatives && result.alternatives.length > 0) && (
              <AlternativeUses
                lang={lang}
                items={result.alternatives}
                hmfaDisplay={lang === "es" ? result.alternatives_hmfa_display_es : result.alternatives_hmfa_display_en}
                sectionNote={lang === "es" ? result.alternatives_section_note_es : result.alternatives_section_note_en}
                onCta={(slug) => openHandoff(slug)}
              />
            )}

            {!handoffOpen && !sent && (
              <div className="mt-8 p-5 md:p-6 bg-white border-2 border-slate-900 rounded-lg" data-testid="provider-next-step">
                <h3 className="text-xl md:text-2xl font-bold leading-tight">{t.owner_next_heading}</h3>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">{t.owner_next_body}</p>
                <button onClick={()=>openHandoff("")} className="mt-4 w-full bg-[#C25E00] hover:bg-[#a24f00] text-white font-semibold rounded-md py-3 min-h-[48px] inline-flex items-center justify-center gap-2" data-testid="provider-handoff-btn">
                  {t.owner_next_button} <ArrowRight className="w-4 h-4"/>
                </button>
              </div>
            )}
            {handoffOpen && !sent && (
              <div id="provider-handoff-form" className="mt-6 p-4 md:p-5 bg-white border border-slate-200 rounded-lg" data-testid="provider-handoff-form">
                <div className="text-sm font-semibold mb-1">{t.owner_form_title}</div>
                <div className="text-xs text-slate-600 mb-3">{t.owner_form_property}: <span className="font-semibold text-slate-900">{form.address}</span> · {form.bedrooms}BR / {form.bathrooms}BA / {form.sqft} sqft</div>
                {clickedUseType && (
                  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="provider-handoff-context">
                    {t.owner_form_asking_about}: <span className="font-semibold">{clickedUseType}</span>
                  </div>
                )}
                <div className="space-y-3">
                  <div>
                    <label htmlFor="oc-name" className="block text-xs font-semibold text-slate-700 mb-1">{t.owner_form_name} <span className="text-red-600" aria-hidden="true">*</span></label>
                    <input id="oc-name" required autoComplete="name" value={contact.name} onChange={e=>setContact({...contact, name:e.target.value})} aria-invalid={!!contactErrors.name} className={inputCls(contactErrors.name)} data-testid="provider-contact-name" />
                    {contactErrors.name && <div role="alert" className="mt-1 text-xs text-red-700" data-testid="provider-contact-name-error">{contactErrors.name}</div>}
                  </div>
                  <div>
                    <label htmlFor="oc-email" className="block text-xs font-semibold text-slate-700 mb-1">{t.owner_form_email} <span className="text-red-600" aria-hidden="true">*</span></label>
                    <input id="oc-email" type="email" inputMode="email" autoComplete="email" required value={contact.email} onChange={e=>setContact({...contact, email:e.target.value})} aria-invalid={!!contactErrors.email} className={inputCls(contactErrors.email)} data-testid="provider-contact-email" />
                    {contactErrors.email ? <div role="alert" className="mt-1 text-xs text-red-700" data-testid="provider-contact-email-error">{contactErrors.email}</div>
                      : <div className="mt-1 text-[11px] text-slate-500">{t.owner_form_email_help}</div>}
                  </div>
                  <div>
                    <label htmlFor="oc-phone" className="block text-xs font-semibold text-slate-700 mb-1">{t.owner_form_phone}</label>
                    <input id="oc-phone" type="tel" inputMode="tel" autoComplete="tel" value={contact.phone} onChange={e=>setContact({...contact, phone:e.target.value})} className={inputCls(false)} data-testid="provider-contact-phone" />
                  </div>
                  <div>
                    <label htmlFor="oc-org" className="block text-xs font-semibold text-slate-700 mb-1">{t.owner_form_org}</label>
                    <input id="oc-org" autoComplete="organization" value={contact.organization} onChange={e=>setContact({...contact, organization:e.target.value})} className={inputCls(false)} data-testid="provider-contact-org" />
                  </div>
                  <div>
                    <label htmlFor="oc-notes" className="block text-xs font-semibold text-slate-700 mb-1">{t.owner_form_notes}</label>
                    <textarea id="oc-notes" value={contact.notes} onChange={e=>setContact({...contact, notes:e.target.value})} rows={3} className={inputCls(false)} data-testid="provider-contact-notes" />
                  </div>
                </div>
                <button onClick={handoff} className="mt-3 w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold rounded-md py-3 min-h-[48px]" data-testid="provider-handoff-submit">{t.owner_form_submit}</button>
              </div>
            )}
            {sent && <div className="mt-6 p-4 bg-emerald-50 border border-emerald-300 rounded-md text-sm text-emerald-900" data-testid="provider-handoff-sent">{t.owner_sent}</div>}
          </section>
        )}
        <footer className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">{t.footer_compliance}</footer>
      </main>
      <PublicFooter lang={lang} />
    </div>
  );
}
